import type { MouseEvent } from "react";
import { MachineServiceItem } from "@infrastructure/stores/machine.store";
import { getInfoByService } from "@domain/enums/local/getInfoByService";
import { useServiceMetadata } from "@boundary/_hooks/useServiceMetadata";
import { ServiceMetadataStore } from "@infrastructure/stores/serviceMetadata.store";
import { Button } from "@boundary/components/button";
import styles from "./serviceSelect.module.scss";
import { Dialog, DialogTrigger } from "@boundary/components/dialog";
import { IconDialog } from "@boundary/widgets/iconDialog";

function QuestionIcon() {
  return (
    <svg
      className={styles.serviceQuestion}
      width="9"
      height="15"
      viewBox="0 0 9 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0.000352911 3.82019C-0.0019189 3.87415 0.00679616 3.92802 0.0259618 3.97847C0.0451275 4.02892 0.0743386 4.07489 0.111799 4.11355C0.14926 4.15221 0.194179 4.18275 0.243793 4.20328C0.293407 4.22382 0.346667 4.23391 0.4003 4.23295H1.76941C1.99843 4.23295 2.18098 4.04412 2.21085 3.81517C2.36021 2.71892 3.107 1.92012 4.43794 1.92012C5.57638 1.92012 6.61857 2.49332 6.61857 3.87199C6.61857 4.93316 5.9979 5.42112 5.01712 6.1631C3.90025 6.98028 3.01572 7.93449 3.07879 9.48362L3.08376 9.84626C3.08551 9.9559 3.12999 10.0605 3.2076 10.1374C3.28522 10.2143 3.38975 10.2574 3.49865 10.2574H4.84453C4.95456 10.2574 5.06009 10.2133 5.1379 10.135C5.2157 10.0566 5.25941 9.95037 5.25941 9.83957V9.6641C5.25941 8.46424 5.71246 8.11497 6.93554 7.18082C7.9462 6.40709 9 5.54813 9 3.74499C9 1.21992 6.88244 0 4.56407 0C2.46144 0 0.158009 0.985962 0.000352911 3.82019ZM2.58424 13.4509C2.58424 14.3416 3.28955 15 4.26037 15C5.27103 15 5.96637 14.3416 5.96637 13.4509C5.96637 12.5284 5.26937 11.88 4.25871 11.88C3.28955 11.88 2.58424 12.5284 2.58424 13.4509Z"
        fill="white"
      />
    </svg>
  );
}

function ServiceItemContent({ title, hint }: { title: string; hint: string | null }) {
  return (
    <div>
      <p>{title}</p>
      {hint && <span className={styles.serviceHint}>{hint}</span>}
    </div>
  );
}

function ServiceInfoButton({ title, description, price, iconUrl }: { title: string; description: string; price: string; iconUrl?: string | null }) {
  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
  };

  return (
    <Dialog modal>
      <DialogTrigger asChild>
        <Button
          variant="secondary"
          size="icon"
          className={styles.serviceInfo}
          onClick={handleClick}
        >
          <QuestionIcon />
        </Button>
      </DialogTrigger>
      <IconDialog
        iconSrc={iconUrl || "/assets/map/printer-bg.png"}
        pill={price}
        title={title}
        description={description}
      />
    </Dialog>
  );
}

function ChevronIcon() {
  return (
    <svg
      className={styles.chevron}
      width="8"
      height="12"
      viewBox="0 0 8 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M1.5 1L6.5 6L1.5 11" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface ServiceSelectProps {
  selected?: string[];
  allowedServices?: string[];
  onClick: (v: string) => void;
  isDisabled?: (v: string) => boolean;
  items?: string[];
  services?: MachineServiceItem[];
  className?: string;
}

export function ServiceSelect({
  selected,
  allowedServices,
  onClick,
  isDisabled,
  items,
  services,
  className,
}: ServiceSelectProps) {
  if (services) {
    const sorted = [...services].sort(
      (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)
    );

    return (
      <ul className={`${styles.services}${className ? ` ${className}` : ""}`}>
        {sorted.map((svc) => {
          const info = getInfoByService(svc.service_type);
          const title = info.title || svc.name;
          const description = svc.description || info.description;
          const price = svc.price_label || info.price;
          const hint = svc.hint ?? info.hint;
          const enabled = svc.enabled ?? info.enabled;

          return (
            <li key={svc.id}>
              <ServiceButton
                title={title}
                hint={hint}
                description={description}
                price={price}
                iconUrl={svc.icon_url}
                selected={selected?.includes(svc.service_type)}
                disabled={!enabled}
                onClick={() => onClick(svc.service_type)}
              />
            </li>
          );
        })}
      </ul>
    );
  }

  const { orderedServiceTypes } = useServiceMetadata();
  const servicesToShow = items ?? orderedServiceTypes;
  const allowed = allowedServices ?? orderedServiceTypes;

  return (
    <ul className={`${styles.services}${className ? ` ${className}` : ""}`}>
      {servicesToShow.map((serviceType) => {
        if (!allowed.includes(serviceType)) return null;

        const info = getInfoByService(serviceType);
        const isSelected = selected?.includes(serviceType);
        const disabled = isDisabled?.(serviceType) ?? !info.enabled;
        const hasVariants = ServiceMetadataStore.getHasVariants(serviceType);

        return (
          <li key={serviceType}>
            <ServiceButton
              title={info.title}
              hint={info.hint}
              description={info.description}
              price={info.price}
              iconUrl={info.iconUrl}
              selected={isSelected}
              disabled={disabled}
              hasVariants={hasVariants}
              onClick={() => onClick(serviceType)}
            />
          </li>
        );
      })}
    </ul>
  );
}

function ServiceButton({
  title, hint, description, price, iconUrl,
  selected, disabled, hasVariants, onClick,
}: {
  title: string;
  hint: string | null;
  description: string;
  price: string;
  iconUrl?: string | null;
  selected?: boolean;
  disabled?: boolean;
  hasVariants?: boolean;
  onClick: () => void;
}) {
  const classes = [
    styles.serviceButton,
    selected && styles.serviceButtonActive,
    disabled && styles.serviceButtonDisabled,
  ].filter(Boolean).join(" ");

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      className={classes}
      onClick={() => { if (!disabled) onClick(); }}
      onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !disabled) { e.preventDefault(); onClick(); } }}
      aria-disabled={disabled}
    >
      <ServiceItemContent title={title} hint={hint} />
      {hasVariants ? (
        <span className={styles.chevronWrap}><ChevronIcon /></span>
      ) : (
        description && !disabled && (
          <ServiceInfoButton title={title} description={description} price={price} iconUrl={iconUrl} />
        )
      )}
    </div>
  );
}
